import { useMsal } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';
import type { ComponentProps } from 'react';

import { ChevronsUpDown, LogOut, SunMoon, User } from '@repo/media/lucide';

import { Button } from '@repo/components/button';
import { Menu, MenuItem, MenuSeparator, MenuTrigger, SubmenuTrigger } from '@repo/components/menu';
import { Popover } from '@repo/components/popover';

import { appVersion } from '~/app-version.ts';
import { useIsSmall } from '~/hooks/use-is-small';
import { queryCurrentUser } from '~/services/authorization/authorization.ts';

import { useLayoutStore } from '../store';

export function AccountButton({ showName }: { showName?: boolean }) {
  const { data: user } = useQuery(queryCurrentUser());
  const { instance } = useMsal();
  const { theme, setTheme } = useLayoutStore();
  const isSmall = useIsSmall();

  if (!user) {
    return null;
  }

  const handleThemeChange: ComponentProps<typeof Menu>['onSelectionChange'] = (selection) => {
    const selected = selection === 'all' ? 'all' : selection.values().next().value;

    if (selected !== 'auto' && selected !== 'light' && selected !== 'dark') {
      console.warn('unsupported theme', selected);
      return;
    }

    setTheme(selected);
    window.document.documentElement.classList.toggle('dark', selected === 'dark');
    window.document.documentElement.classList.toggle('light', selected === 'light');
  };

  return (
    <MenuTrigger>
      {showName
        ? (
            <Button
              variant="ghost"
              color="secondary"
              className="w-full justify-start"
              size="md"
              icon={{ component: <User size={16} strokeWidth={1.5} aria-label="Toggle account menu" /> }}
            >
              <span className="truncate flex-1 text-left text-sm">{user.name}</span>
              <ChevronsUpDown className="!size-3.5 text-secondary-400 ml-auto shrink-0" />
            </Button>
          )
        : (
            <Button
              size="icon-md"
              icon={{ component: <User size={30} strokeWidth={1.5} aria-label="Toggle account menu" /> }}
              variant="soft"
            />
          )}
      <Popover>
        <div className="p-2">
          <div className="px-2 pt-1 text-lg">
            {user.name}
          </div>
          <div className="px-2 pt-0 text-secondary-600 text-sm">
            {user.email}
          </div>
          <div className="px-2 pb-1 text-secondary-400 text-xs">
            {`Version ${appVersion}`}
          </div>
          <Menu className="-mx-2">
            <MenuSeparator className="mb-2 mx-2" />
            <SubmenuTrigger>
              <MenuItem>
                <SunMoon strokeWidth={1.5} size={24} />
                Theme
              </MenuItem>
              <Popover {...(isSmall ? { placement: 'bottom', offset: -1 } : {})}>
                <Menu
                  selectedKeys={[theme]}
                  selectionMode="single"
                  disallowEmptySelection
                  onSelectionChange={handleThemeChange}
                >
                  <MenuItem id="auto">System</MenuItem>
                  <MenuItem id="light">Light</MenuItem>
                  <MenuItem id="dark">Dark</MenuItem>
                </Menu>
              </Popover>
            </SubmenuTrigger>
            <MenuItem
              onAction={() => {
                void instance.logoutRedirect();
              }}
            >
              <LogOut strokeWidth={1.5} size={20} />
              Sign out
            </MenuItem>
          </Menu>
        </div>
      </Popover>
    </MenuTrigger>
  );
}
